import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronRight,
  Eye,
  LifeBuoy,
  LogOut,
  PenSquare,
  Shield,
  Star,
  Trash2,
  User,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuthStore } from '@/stores/authStore';
import { useAccessRequestStore } from '@/stores/accessRequestStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useProfileStore } from '@/stores/profileStore';
import { Card, CardContent, Avatar, Button } from '@/components/ui';
import { ProfileBottomSheet } from '@/components/profile/ProfileBottomSheet';
import { db } from '@/lib/firebase';
import { getNotificationPermissionState, resolvePushEnabled } from '@/lib/messaging';

const SETTINGS_DOC_ID = 'preferences';
const DEFAULT_VERIFICATION_BADGES = {
  identity: false,
  education: false,
  income: false,
  spermTest: false,
  stdTest: false,
};

function calculateAge(birthDate?: Date): number | null {
  if (!birthDate) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function MyPage() {
  const navigate = useNavigate();
  const { user, userData, signOut } = useAuthStore();
  const profile = useProfileStore((state) => state.profile);
  const fetchProfile = useProfileStore((state) => state.fetchProfile);
  const unreadVerificationResultCount = useNotificationStore(
    (state) => state.unreadVerificationResultCount
  );
  const receivedAccessRequests = useAccessRequestStore((state) => state.requests);
  const fetchReceivedAccessRequests = useAccessRequestStore((state) => state.fetchReceivedRequests);
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermissionState());
  const [profileVerificationBadges, setProfileVerificationBadges] = useState(
    DEFAULT_VERIFICATION_BADGES
  );

  const verificationBadgeText =
    unreadVerificationResultCount > 99 ? '99+' : String(unreadVerificationResultCount);
  const pendingAccessRequestCount = useMemo(
    () => receivedAccessRequests.filter((request) => request.status === 'pending').length,
    [receivedAccessRequests]
  );
  const accessRequestBadgeText =
    pendingAccessRequestCount > 99 ? '99+' : String(pendingAccessRequestCount);

  useEffect(() => {
    setNotificationPermission(getNotificationPermissionState());
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    if (profile?.userId === user.uid) {
      return;
    }

    void fetchProfile(user.uid);
  }, [fetchProfile, profile?.userId, user?.uid]);

  useEffect(() => {
    const loadPushSettings = async () => {
      if (!user?.uid) {
        setPushEnabled(null);
        return;
      }

      try {
        const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
        const settingsSnap = await getDoc(settingsRef);
        const rawNotifications = settingsSnap.data()?.notifications;
        setPushEnabled(resolvePushEnabled(rawNotifications));
      } catch (error) {
        console.error('Error loading notification settings summary:', error);
        setPushEnabled(null);
      }
    };

    void loadPushSettings();
  }, [user?.uid]);

  useEffect(() => {
    const loadProfileVerificationBadges = async () => {
      if (!user?.uid) {
        setProfileVerificationBadges(DEFAULT_VERIFICATION_BADGES);
        return;
      }

      try {
        const profileRef = doc(db, 'profiles', user.uid);
        const profileSnap = await getDoc(profileRef);
        const rawBadges = profileSnap.data()?.verificationBadges as
          | {
              identity?: unknown;
              education?: unknown;
              income?: unknown;
              spermTest?: unknown;
              stdTest?: unknown;
            }
          | undefined;

        setProfileVerificationBadges({
          identity: rawBadges?.identity === true,
          education: rawBadges?.education === true,
          income: rawBadges?.income === true,
          spermTest: rawBadges?.spermTest === true,
          stdTest: rawBadges?.stdTest === true,
        });
      } catch (error) {
        console.error('Error loading profile verification badges:', error);
        setProfileVerificationBadges(DEFAULT_VERIFICATION_BADGES);
      }
    };

    void loadProfileVerificationBadges();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || userData?.role !== 'donor') {
      return;
    }
    void fetchReceivedAccessRequests(user.uid);
  }, [user?.uid, userData?.role, fetchReceivedAccessRequests]);

  const age = useMemo(
    () => calculateAge(profile?.basicInfo?.birthDate),
    [profile?.basicInfo?.birthDate]
  );
  const ageLabel = useMemo(() => {
    if (age === null) {
      return userData?.role === 'donee' ? '年代未設定' : '年齢未設定';
    }
    if (userData?.role === 'donee') {
      const decade = Math.floor(age / 10) * 10;
      return decade >= 10 ? `${decade}代` : '10代未満';
    }
    return `${age}歳`;
  }, [age, userData?.role]);
  const residenceLabel = profile?.basicInfo?.residence?.trim() || '居住地未設定';
  const heightLabel =
    typeof profile?.physicalInfo?.height === 'number'
      ? `${profile.physicalInfo.height}cm`
      : '身長未設定';
  const profileMetaParts = useMemo(
    () =>
      userData?.role === 'donor'
        ? [ageLabel, residenceLabel, heightLabel]
        : [ageLabel, residenceLabel],
    [ageLabel, heightLabel, residenceLabel, userData?.role]
  );
  const verificationDisplayItems = useMemo(
    () =>
      userData?.role === 'donor'
        ? [
            { key: 'identity', label: '本人', verified: userData?.identityVerified || profileVerificationBadges.identity },
            { key: 'education', label: '学歴', verified: profileVerificationBadges.education },
            { key: 'income', label: '年収', verified: profileVerificationBadges.income },
            { key: 'spermTest', label: '精液', verified: profileVerificationBadges.spermTest },
            { key: 'stdTest', label: '性病', verified: profileVerificationBadges.stdTest },
          ]
        : [
            { key: 'identity', label: '本人', verified: userData?.identityVerified || profileVerificationBadges.identity },
          ],
    [profileVerificationBadges, userData?.identityVerified, userData?.role]
  );

  const notificationStatusText = useMemo(() => {
    if (notificationPermission === 'unsupported') {
      return 'この端末ではプッシュ通知に対応していません';
    }

    if (notificationPermission !== 'granted') {
      return 'ブラウザ通知が未許可です';
    }

    if (pushEnabled === false) {
      return 'プッシュ通知がOFFです';
    }

    return 'プッシュ通知は有効です';
  }, [notificationPermission, pushEnabled]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      <div className="min-h-full bg-[#F7F4EF]">
        <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
          <div className="px-5 pb-5 pt-14 md:px-0 md:pt-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">MY PAGE</p>
            <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">マイページ</h1>
          </div>

          <div className="space-y-6 px-4 pb-12 md:px-0 md:pb-8">
            <Card className="overflow-hidden border-[#ECE6DD] bg-white">
              <CardContent className="flex items-start gap-3.5 px-4 py-4">
                <Avatar
                  src={userData?.iconUrl}
                  size="sm"
                  className="h-[52px] w-[52px] rounded-2xl bg-[#D4C5A9]"
                />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <p className="truncate text-[15px] font-semibold leading-tight text-[#1A1A1A]">
                    {userData?.displayName || 'ゲスト'}
                  </p>
                  <p className="text-[12px] text-[#888]">{profileMetaParts.join(' · ')}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-0.5">
                    {verificationDisplayItems.map((badge) => (
                      <div key={badge.key} className="flex items-center gap-1">
                        <span
                          className="inline-block h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: badge.verified ? '#B87333' : '#DEDBD6' }}
                        />
                        <span
                          className="text-[11px]"
                          style={{
                            color: badge.verified ? '#B87333' : '#C0BDB8',
                            fontWeight: badge.verified ? 500 : 400,
                          }}
                        >
                          {badge.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
              <div className="border-t border-[#F0EDE8] px-4 py-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="!h-10 !rounded-full"
                    leftIcon={<Eye className="h-4 w-4" />}
                    onClick={() => setIsProfileSheetOpen(true)}
                  >
                    確認
                  </Button>
                  <Link to="/mypage/edit" className="block">
                    <Button
                      type="button"
                      className="!h-10 w-full !rounded-full"
                      leftIcon={<PenSquare className="h-4 w-4" />}
                    >
                      編集
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-[#ECE6DD]">
              <Link to="/mypage/verification" className="block">
                <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                    <Shield className="h-[18px] w-[18px] text-copper" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[15px] font-medium text-[#1A1A1A]">認証バッジ管理</p>
                      {unreadVerificationResultCount > 0 && (
                        <span className="inline-flex h-[1.1rem] min-w-[1.1rem] items-center justify-center rounded-full bg-copper px-1 text-[10px] font-bold leading-none text-white">
                          {verificationBadgeText}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[12px] text-[#999]">本人確認・各種証明書</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                </CardContent>
              </Link>
              <div className="ml-4 h-px bg-[#F0EDE8]" />
              <Link to="/mypage/reviews" className="block">
                <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                    <Star className="h-[18px] w-[18px] text-copper" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-[#1A1A1A]">レビュー管理</p>
                    <p className="mt-0.5 text-[12px] text-[#999]">受け取ったレビューを確認</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                </CardContent>
              </Link>
              {userData?.role === 'donor' && (
                <>
                  <div className="ml-4 h-px bg-[#F0EDE8]" />
                  <Link to="/mypage/requests" className="block">
                    <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                        <Eye className="h-[18px] w-[18px] text-copper" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-[15px] font-medium text-[#1A1A1A]">閲覧申請管理</p>
                          {pendingAccessRequestCount > 0 && (
                            <span className="inline-flex h-[1.1rem] min-w-[1.1rem] items-center justify-center rounded-full bg-copper px-1 text-[10px] font-bold leading-none text-white">
                              {accessRequestBadgeText}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[12px] text-[#999]">閲覧申請の承認・拒否</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                    </CardContent>
                  </Link>
                </>
              )}
            </Card>

            <div>
              <p className="mb-1.5 px-4 text-[12px] font-medium uppercase tracking-wider text-[#999]">設定</p>
              <Card className="overflow-hidden">
                <Link to="/mypage/notifications" className="block">
                  <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                      <Bell className="h-[18px] w-[18px] text-[#888]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-[#1A1A1A]">通知設定</p>
                      <p className="mt-0.5 text-[12px] text-[#999]">{notificationStatusText}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                  </CardContent>
                </Link>
                <div className="ml-4 h-px bg-[#F0EDE8]" />
                <Link to="/mypage/account" className="block">
                  <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                      <User className="h-[18px] w-[18px] text-[#888]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-[#1A1A1A]">アカウント設定</p>
                      <p className="mt-0.5 text-[12px] text-[#999]">メールアドレス・パスワード</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                  </CardContent>
                </Link>
                <div className="ml-4 h-px bg-[#F0EDE8]" />
                <Link to="/mypage/contact" className="block">
                  <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                      <LifeBuoy className="h-[18px] w-[18px] text-[#888]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-[#1A1A1A]">お問い合わせ</p>
                      <p className="mt-0.5 text-[12px] text-[#999]">サポートに連絡する</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                  </CardContent>
                </Link>
              </Card>
            </div>

            <Card className="overflow-hidden border-[#ECE6DD]">
              <button type="button" onClick={handleSignOut} className="block w-full">
                <CardContent className="flex items-center gap-3.5 px-4 py-3.5 text-left">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F5F2ED]">
                    <LogOut className="h-[18px] w-[18px] text-[#888]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-[#1A1A1A]">ログアウト</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                </CardContent>
              </button>
            </Card>

            <div>
              <p className="mb-1.5 px-4 text-[12px] font-medium uppercase tracking-wider text-[#999]">
                注意が必要な操作
              </p>
              <Link to="/mypage/withdraw">
                <Card className="mb-20 overflow-hidden border-red-200 md:mb-8">
                  <CardContent className="flex items-center gap-3.5 px-4 py-3.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-50">
                      <Trash2 className="h-[18px] w-[18px] text-red-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-red-600">退会する</p>
                      <p className="mt-0.5 text-[12px] text-[#999]">アカウントを削除します</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#C8C4BC]" />
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ProfileBottomSheet
        userId={user?.uid ?? null}
        isOpen={isProfileSheetOpen}
        onClose={() => setIsProfileSheetOpen(false)}
      />
    </>
  );
}
